import { graphql } from 'react-relay';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import WatchDialogComponentWithWatchChildrenQuery, {
	type WatchDialogComponentWithWatchChildrenQuery$data,
	type WatchDialogComponentWithWatchChildrenQuery$variables,
} from './__generated__/WatchDialogComponentWithWatchChildrenQuery.graphql.ts';

import type {
	preloadWatchDialogWithSpaceKeyQuery$data,
	preloadWatchDialogWithSpaceKeyQuery$variables,
} from './__generated__/preloadWatchDialogWithSpaceKeyQuery.graphql.ts';

export type preloadWatchDialogProps = {
	spaceKey?: string;
	isLicensed: boolean;
	userId: string | null;
	contentId?: string;
};

export const preloadWatchDialogWithSpaceKeyQuery = graphql`
	query preloadWatchDialogWithSpaceKeyQuery($spaceKey: String!) {
		space(key: $spaceKey) {
			id
			key
			currentUser {
				isWatched
				isWatchingBlogs
			}
			operations {
				operation
				targetType
			}
		}
		spaceHomepage(spaceKey: $spaceKey) {
			id
			type
			currentUserIsWatching
		}
	}
`;

export function preloadWatchDialog({
	spaceKey,
	isLicensed = false,
	userId,
	contentId,
}: preloadWatchDialogProps) {
	// do nothing if user is unlicensed, no userId is provided, or spaceKey and contentId are BOTH undefined
	if (!isLicensed || !userId || (!spaceKey && !contentId)) {
		return Promise.resolve({});
	}
	// the only scenario in which spaceKey is passed but contentId is not is in Space Overview via preloadGroupedButtons
	if (spaceKey && !contentId) {
		return query<
			preloadWatchDialogWithSpaceKeyQuery$data,
			preloadWatchDialogWithSpaceKeyQuery$variables
		>({
			query: preloadWatchDialogWithSpaceKeyQuery,
			variables: { spaceKey },
			fetchPolicy: 'network-only',
		}).then(({ data }): Promise<any> | void => {
			if (data?.spaceHomepage?.id) {
				writeQueryInterop<
					WatchDialogComponentWithWatchChildrenQuery$data,
					WatchDialogComponentWithWatchChildrenQuery$variables
				>({
					query: WatchDialogComponentWithWatchChildrenQuery,
					variables: {
						contentId: data?.spaceHomepage?.id,
					},
					data: {
						singleContent:
							data?.spaceHomepage && data?.space
								? {
										currentUserIsWatching: data?.spaceHomepage?.currentUserIsWatching,
										currentUserHasAncestorWatchingChildren: undefined,
										currentUserIsWatchingChildren: undefined,
										id: data?.spaceHomepage?.id,
										space: data?.space,
										type: data?.spaceHomepage?.type,
										__typename: 'Content',
									}
								: null,
					},
				});
			}
		});
	}

	return query<
		WatchDialogComponentWithWatchChildrenQuery$data,
		WatchDialogComponentWithWatchChildrenQuery$variables
	>({
		query: WatchDialogComponentWithWatchChildrenQuery,
		variables: { contentId: contentId! },
		// Must be preloaded with network only to get fresh data
		fetchPolicy: 'network-only',
	});
}
